import React from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import {
  fetchPrepCategories,
  updatePrepItemCategories,
  type PrepCategoryWithCount,
  type PrepDetailResponse,
  type PrepListItem,
} from '../../api/prep'

export interface EditPrepCategoriesModalProps {
  open: boolean
  item: PrepListItem | PrepDetailResponse
  onClose: () => void
  onSaved: (updated: PrepDetailResponse | PrepListItem) => void
}

const EditPrepCategoriesModal: React.FC<EditPrepCategoriesModalProps> = ({ open, item, onClose, onSaved }) => {
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [categories, setCategories] = React.useState<PrepCategoryWithCount[]>([])
  const [selected, setSelected] = React.useState<Set<number>>(new Set())

  const applyInitial = React.useCallback((source: PrepListItem | PrepDetailResponse) => {
    const current = new Set<number>()
    if (Array.isArray(source.categories)) {
      source.categories.forEach((category) => {
        if (category && typeof category.id === 'number' && Number.isFinite(category.id) && category.id > 0) {
          current.add(Math.floor(category.id))
        }
      })
    }
    setSelected(current)
  }, [])

  React.useEffect(() => {
    if (!open) {
      return
    }
    applyInitial(item)
  }, [applyInitial, item, open])

  React.useEffect(() => {
    if (!open) {
      return
    }
    let cancelled = false
    const controller = new AbortController()
    setLoading(true)
    setError(null)

    fetchPrepCategories({ scope: 'global', includeEmpty: true }, { signal: controller.signal })
      .then((response) => {
        if (cancelled || controller.signal.aborted) {
          return
        }
        const cats = Array.isArray(response.categories) ? response.categories.slice() : []
        cats.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        setCategories(cats)
      })
      .catch((err) => {
        if (cancelled || controller.signal.aborted) {
          return
        }
        const message = err instanceof Error ? err.message : 'Unable to load categories.'
        setError(message)
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [open])

  const toggleSelection = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleSave = async () => {
    if (saving) {
      return
    }
    const categoryIds = Array.from(selected)
    setSaving(true)
    setError(null)
    try {
      const updated = await updatePrepItemCategories(item.id, categoryIds)
      onSaved(updated)
      onClose()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to update categories.'
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" aria-labelledby="edit-prep-categories-title">
      <DialogTitle id="edit-prep-categories-title">Edit categories</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {loading ? (
            <Stack spacing={1}>
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} variant="rounded" height={32} />
              ))}
            </Stack>
          ) : (
            <Stack spacing={0.5}>
              {categories.map((category) => (
                <FormControlLabel
                  key={category.id}
                  control={
                    <Checkbox
                      checked={selected.has(category.id)}
                      onChange={() => toggleSelection(category.id)}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {category.name}
                      </Typography>
                      {typeof category.count === 'number' ? (
                        <Typography variant="body2" color="text.secondary">
                          {category.count}
                        </Typography>
                      ) : null}
                    </Box>
                  }
                />
              ))}
              {categories.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No categories available.
                </Typography>
              ) : null}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditPrepCategoriesModal
